import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from '../../lib/store';
import { isCurrentAccountFailed } from '../../lib/store/account-flow/selectors';
import { getCurrentAccountTradingPlan } from '../../lib/store/crm/helpers';

import { useElementVisibilityById } from '../../hooks';
import { ACCOUNT_SETUP_NOTIFICATION_ID } from '../AccountSetupNotification/AccountSetupNotification';
import MaxDrawdownReachedModal from '../MaxDrawdownReachedModal/MaxDrawdownReachedModal';
import PlanTargetModal from '../PlanTargetModal/PlanTargetModal';
import PostChallengeDocumentsApprovedModal from '../PostChallengeDocumentsApprovedModal/PostChallengeDocumentsApprovedModal';
import ResetDailyLossConfirmModal from '../ResetDailyLossConfirmModal/ResetDailyLossConfirmModal';
import RewindTradingDayModal from '../RewindTradingDayModal/RewindTradingDayModal';

type Props = PropsFromRedux;

const CloseoutManager = (props: Props) => {
  const {
    accountStatusData,
    currentTradingPlan,
    isAccountFailed,
    isSwitchingAccount,
  } = props;
  const [isMaxDrawdownModalOpen, setIsMaxDrawdownModalOpen] = useState<boolean>(false);
  const [warningLevel, setWarningLevel] = useState<50 | 75 | null>(null);
  const [isDailyLossDismissed, setIsDailyLossDismissed] = useState<boolean>(false);
  const isRewindModalShowed = useElementVisibilityById('rewindTradingDayModal');
  const isAccountSetupNotificationVisible = useElementVisibilityById(ACCOUNT_SETUP_NOTIFICATION_ID);

  const {
    dailyLoss,
    dailyLossReached,
    totalLossReached,
    dailyResetProcessing,
  } = accountStatusData || {};
  useEffect(() => {
    if (isMaxDrawdownModalOpen) {
      return;
    }

    // PRIORITY 1: Check if max drawdown (total loss) is reached
    if (totalLossReached || isAccountFailed) {
      if (!dailyResetProcessing && !isAccountSetupNotificationVisible && !isSwitchingAccount) {
        setIsMaxDrawdownModalOpen(true);
        setWarningLevel(null);
      }

      return;
    }

    // PRIORITY 2: Check if daily loss limit is reached via SSE event
    if (dailyLossReached) {
      setWarningLevel(null);
      setIsMaxDrawdownModalOpen(false);
      return;
    }

    // PRIORITY 3: Calculate percentage for daily loss warnings
    const dailyLossLimit = currentTradingPlan?.daily_loss_limit_amount || 0;

    const lossPercentage = dailyLoss < 0
      ? Math.abs(dailyLoss / dailyLossLimit) * 100
      : 0;
    // Determine warning level
    if (lossPercentage >= 75) {
      setWarningLevel(75);
      setIsMaxDrawdownModalOpen(false);
    } else if (lossPercentage >= 50) {
      setWarningLevel(50);
      setIsMaxDrawdownModalOpen(false);
    } else {
      setIsMaxDrawdownModalOpen(false);
      setWarningLevel(null);
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    accountStatusData,
    currentTradingPlan,
    isAccountFailed,
    isAccountSetupNotificationVisible,
    isSwitchingAccount,
  ]);

  const handleModalToggle = () => {
    if (warningLevel) {
      setWarningLevel(null);
    }
    if (dailyLossReached) {
      setIsDailyLossDismissed(true);
    }
  };

  const handleMaxDrawdownModalToggle = () => {
    setIsMaxDrawdownModalOpen(false);
  };

  return (
    <>
      <MaxDrawdownReachedModal
        isOpen={isMaxDrawdownModalOpen}
        toggleModal={handleMaxDrawdownModalToggle}
      />
      <ResetDailyLossConfirmModal
        isOpen={(
          dailyResetProcessing !== undefined
          && !isDailyLossDismissed
          && !dailyResetProcessing
          && !totalLossReached
          && !isRewindModalShowed
          && (!!dailyLossReached || !!warningLevel))}
        toggleModal={handleModalToggle}
        isDailyLossReached={dailyLossReached}
        warningLevel={warningLevel}
      />
      <PlanTargetModal />
      <PostChallengeDocumentsApprovedModal />
      <RewindTradingDayModal />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountStatusData: state.closeout.accountStatusData,
  currentTradingPlan: getCurrentAccountTradingPlan(state.crm),
  isAccountFailed: isCurrentAccountFailed(state),
  isSwitchingAccount: state.crm.isSwitchingAccount,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CloseoutManager);
